import React, { useMemo, useState, useEffect } from 'react';
import { TrendingUp, BarChart3, LineChart as LineIcon } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import DashboardBottomPanel from './DashboardBottomPanel';
import { MONTH_MS, QUARTER_MS } from '../../constants';

/**
 * APEX v7.0 — Dashboard Center Panel
 *
 * Centre du Dashboard : courbe LP hero + bloc actif (slot).
 * Les KPIs sont au-dessus (KpiTilesRow), les insights sont dans DashboardBottomPanel.
 */


// V7.2.5 : helper pour convertir tier+rank+lp en score linéaire
// Identique à scoreOf de computeLpDeltasByGame dans analytics.js
function scoreOf(s) {
  const tierBase = {
    IRON: 0, BRONZE: 400, SILVER: 800, GOLD: 1200, PLATINUM: 1600,
    EMERALD: 2000, DIAMOND: 2400, MASTER: 2800, GRANDMASTER: 2800, CHALLENGER: 2800
  };
  const rankBase = { IV: 0, III: 100, II: 200, I: 300 };
  const base = tierBase[s.tier] || 0;
  const rankAdd = ['MASTER', 'GRANDMASTER', 'CHALLENGER'].includes(s.tier) ? 0 : (rankBase[s.rank] || 0);
  return base + rankAdd + (s.lp || 0);
}

function computeLpHistory(rankSnapshots, withinMs) {
  if (!Array.isArray(rankSnapshots) || rankSnapshots.length === 0) return [];
  const cutoff = Date.now() - withinMs;

  // V7.4 : downsample à 1 snapshot par jour (le dernier de chaque journée)
  // Évite le bruit visuel quand on a beaucoup de snapshots (jusqu'à 96/30j en pratique)
  const inRange = rankSnapshots
    .filter(s => new Date(s.captured_at) > cutoff)
    .sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));

  // Grouper par jour (YYYY-MM-DD) et garder le dernier de chaque jour
  const byDay = {};
  inRange.forEach(s => {
    const dayKey = new Date(s.captured_at).toISOString().slice(0, 10);
    // On garde toujours le plus récent du jour (puisque trié par ordre croissant, le dernier écrase)
    byDay[dayKey] = s;
  });

  return Object.entries(byDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, s]) => ({
      x: new Date(s.captured_at).getTime(),
      y: scoreOf(s),
      dayKey,
      // V7.5.0 : on conserve les vrais tier/rank/lp pour le tooltip
      tier: s.tier,
      rank: s.rank,
      lp: s.lp
    }));
}

// V8.11 — Vue delta LP par jour (depuis lpPoints downsamplés)
function LpDailyDeltaChart({ points }) {
  if (!points || points.length < 2) {
    return (
      <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-zinc-500 text-xs">
        <BarChart3 size={28} className="text-zinc-700" />
        <div className="font-mono">Pas assez de snapshots pour calculer les deltas</div>
      </div>
    );
  }

  // Convertir lpPoints en deltas jour à jour
  const deltas = points.slice(1).map((p, i) => {
    const prev = points[i];
    const delta = p.y - prev.y;
    const date = new Date(p.x).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    return { date, delta, label: p.label || '' };
  });

  function DeltaTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const isPos = d.delta > 0;
    const isZero = d.delta === 0;
    return (
      <div className="bg-zinc-950 border border-zinc-700 p-3 text-xs font-mono">
        <div className="text-zinc-500 mb-1">{d.date}</div>
        <div className={`font-bold ${isPos ? 'text-cyan-400' : isZero ? 'text-zinc-400' : 'text-rose-400'}`}>
          {isPos ? '+' : ''}{d.delta} LP
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={deltas}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
        <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: 10, fontFamily: 'monospace' }} />
        <YAxis stroke="#71717a" style={{ fontSize: 11, fontFamily: 'monospace' }} />
        <Tooltip content={<DeltaTooltip />} cursor={{ fill: 'rgba(167, 139, 250, 0.05)' }} />
        <ReferenceLine y={0} stroke="#52525b" strokeWidth={1} />
        <Bar dataKey="delta" radius={[2, 2, 0, 0]}>
          {deltas.map((entry, idx) => (
            <Cell
              key={idx}
              fill={entry.delta > 0 ? '#22d3ee' : entry.delta < 0 ? '#fb7185' : '#52525b'}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function LpHeroCurve({ points, periodLabel }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!points || points.length < 2) {
    return (
      <div className="h-[280px] flex flex-col items-center justify-center gap-2 text-zinc-500 text-xs">
        <TrendingUp size={28} className="text-zinc-700" />
        <div className="font-mono">Pas assez de snapshots pour tracer la courbe</div>
        <div className="text-[10px] text-zinc-600">Importe des games pour générer des snapshots de rang.</div>
      </div>
    );
  }

  const W = 1200, H = 280, paddingX = 16, paddingTop = 24, paddingBottom = 28;
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMinRaw = Math.min(...ys);
  const yMaxRaw = Math.max(...ys);
  const yPad = Math.max(20, (yMaxRaw - yMinRaw) * 0.15);
  const yMin = yMinRaw - yPad;
  const yMax = yMaxRaw + yPad;
  const yRange = Math.max(1, yMax - yMin);
  const xRange = Math.max(1, xMax - xMin);

  const norm = (p) => ({
    x: paddingX + ((p.x - xMin) / xRange) * (W - paddingX * 2),
    y: paddingTop + (1 - (p.y - yMin) / yRange) * (H - paddingTop - paddingBottom)
  });
  const np = points.map(norm);
  // V7.4 : Smoothing via cubic bezier (Catmull-Rom-like)
  // Génère une courbe lisse au lieu de segments anguleux.
  function buildSmoothPath(points) {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;

    let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    const tension = 0.15; // 0 = anguleux, 0.5 = très smooth ; 0.15 = fidèle aux variations

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;

      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  }

  const lineD = buildSmoothPath(np);
  const areaD = `${lineD} L ${np[np.length - 1].x.toFixed(1)} ${H - paddingBottom} L ${np[0].x.toFixed(1)} ${H - paddingBottom} Z`;

  const lastPt = np[np.length - 1];
  const firstY = ys[0];
  const lastY = ys[ys.length - 1];
  const delta = lastY - firstY;

  const minIdx = ys.indexOf(yMinRaw);
  const maxIdx = ys.indexOf(yMaxRaw);
  const minPt = np[minIdx];
  const maxPt = np[maxIdx];
  const showMinMax = maxIdx !== minIdx;

  const formatDate = (ts) => new Date(ts).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[280px]">
        <defs>
          <linearGradient id="lp-hero-area" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(p => (
          <line
            key={p}
            x1={paddingX}
            x2={W - paddingX}
            y1={paddingTop + (H - paddingTop - paddingBottom) * p}
            y2={paddingTop + (H - paddingTop - paddingBottom) * p}
            stroke="#27272a"
            strokeWidth="0.5"
            strokeDasharray="2 4"
          />
        ))}
        <path d={areaD} fill="url(#lp-hero-area)" />
        <path d={lineD} fill="none" stroke="#a78bfa" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>

      {/* V7.4.5 : Dots HTML overlay (au-dessus du SVG) — restent parfaitement ronds
          car non affectés par preserveAspectRatio="none" du SVG */}
      {np.map((pt, idx) => {
        const isHovered = hoveredIdx === idx;
        const ys = points.map(p => p.y);
        const isMin = ys[idx] === Math.min(...ys);
        const isMax = ys[idx] === Math.max(...ys);
        const isLast = idx === np.length - 1;
        // Couleur selon le statut du point
        let dotColor = '#a78bfa';
        if (isLast) dotColor = '#a78bfa';
        else if (isMax && np.length > 2) dotColor = '#22d3ee';
        else if (isMin && np.length > 2) dotColor = '#fb7185';

        return (
          <div
            key={idx}
            className="absolute pointer-events-auto"
            style={{
              left: `${(pt.x / W) * 100}%`,
              top: `${(pt.y / H) * 100}%`,
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
              zIndex: isHovered ? 15 : (isLast || isMax || isMin) ? 11 : 10
            }}
            onMouseEnter={() => setHoveredIdx(idx)}
            onMouseLeave={() => setHoveredIdx(null)}
          >
            {/* Zone de hover invisible plus large */}
            <div
              className="absolute rounded-full"
              style={{
                width: 24,
                height: 24,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
            {/* Halo au hover */}
            {isHovered && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: 22,
                  height: 22,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: dotColor,
                  opacity: 0.3
                }}
              />
            )}
            {/* Dot principal (toujours rond) */}
            <div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: isLast ? 10 : (isHovered ? 10 : 8),
                height: isLast ? 10 : (isHovered ? 10 : 8),
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: dotColor,
                border: '1.5px solid #0a0a0a',
                transition: 'width 0.15s, height 0.15s'
              }}
            />
            {/* Halo permanent pour le dernier point */}
            {isLast && (
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  width: 20,
                  height: 20,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: dotColor,
                  opacity: 0.3,
                  zIndex: -1
                }}
              />
            )}
          </div>
        );
      })}

      {/* V7.4.5 : Dates en HTML, plus lisibles */}
      <div className="absolute bottom-1 left-2 text-[11px] text-zinc-400 font-mono pointer-events-none">
        {formatDate(xMin)}
      </div>
      <div className="absolute bottom-1 right-2 text-[11px] text-zinc-400 font-mono pointer-events-none">
        {formatDate(xMax)}
      </div>

      {/* V7.4.1 : Tooltip overlay au hover d'un snapshot */}
      {hoveredIdx !== null && np[hoveredIdx] && (
        <div
          className="absolute pointer-events-none bg-zinc-950 border border-violet-400/50 px-2.5 py-1.5 z-20"
          style={{
            left: `${(np[hoveredIdx].x / W) * 100}%`,
            top: `${(np[hoveredIdx].y / H) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 12px))',
            boxShadow: '0 0 20px -5px rgba(167, 139, 250, 0.4)'
          }}
        >
          <div className="text-base font-mono font-black text-violet-400 leading-none">
            {points[hoveredIdx].lp ?? 0} LP
          </div>
          <div className="text-[10px] text-violet-300/80 font-mono font-bold mt-0.5 whitespace-nowrap">
            {points[hoveredIdx].tier}
            {points[hoveredIdx].rank && !['MASTER','GRANDMASTER','CHALLENGER'].includes(points[hoveredIdx].tier)
              ? ` ${points[hoveredIdx].rank}`
              : ''}
          </div>
          <div className="text-[9px] text-zinc-500 font-mono mt-0.5 whitespace-nowrap">
            {new Date(points[hoveredIdx].x).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
      )}

      <div className="absolute top-0 right-0 text-right">
        <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono">{periodLabel}</div>
        <div className={`text-2xl font-mono font-black ${delta > 0 ? 'text-cyan-400' : delta < 0 ? 'text-rose-400' : 'text-zinc-400'}`}>
          {delta > 0 ? '+' : ''}{delta} LP
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">
          {points.length} jours
        </div>
      </div>
    </div>
  );
}

export default function DashboardCenterPanel({
  rankSnapshots = [],
  activeBlockSlot,
  // v8.0 : props pour le widget champions + insights sous la courbe LP
  games = [],
  version,
  userSettings,
  onSuggestionClick,
  onSeeAll,
}) {
  const safeSnapshots = Array.isArray(rankSnapshots) ? rankSnapshots : [];

  // V7.4.4 : calcule la durée couverte par les snapshots
  const dataAgeDays = useMemo(() => {
    if (safeSnapshots.length === 0) return 0;
    const oldestTs = Math.min(...safeSnapshots.map(s => new Date(s.captured_at).getTime()));
    return Math.floor((Date.now() - oldestTs) / (1000 * 60 * 60 * 24));
  }, [safeSnapshots]);

  // V7.4.4 : périodes disponibles selon les données réelles
  const availablePeriods = useMemo(() => {
    const periods = [];
    if (dataAgeDays >= 30) periods.push({ value: '30d', label: '30J', windowMs: MONTH_MS, periodLabel: '30 DERNIERS JOURS' });
    if (dataAgeDays >= 90) periods.push({ value: '90d', label: '90J', windowMs: QUARTER_MS, periodLabel: '90 DERNIERS JOURS' });
    // MAX toujours disponible
    periods.push({
      value: 'max',
      label: 'MAX',
      windowMs: Infinity,
      periodLabel: dataAgeDays > 0 ? `MAX (${dataAgeDays} JOURS)` : 'MAX'
    });
    return periods;
  }, [dataAgeDays]);

  // V7.4.4 : période par défaut = la plus large dispo en-dessous de 90J
  // (si tu as 35 jours, default = 30J ; si tu as 5 jours, default = MAX)
  const defaultPeriod = useMemo(() => {
    if (dataAgeDays >= 30) return '30d';
    return 'max';
  }, [dataAgeDays]);

  const [period, setPeriod] = useState(defaultPeriod);
  // V8.11 : toggle entre courbe lissée et delta jour
  const [view, setView] = useState('curve');

  // Si la période active disparaît (ex: nouvelle install, plus assez de données), fallback sur defaultPeriod
  useEffect(() => {
    if (!availablePeriods.some(p => p.value === period)) {
      setPeriod(defaultPeriod);
    }
  }, [availablePeriods, period, defaultPeriod]);

  const activePeriod = availablePeriods.find(p => p.value === period) || availablePeriods[0];

  const lpPoints = useMemo(() => {
    if (!activePeriod) return [];
    return computeLpHistory(safeSnapshots, activePeriod.windowMs);
  }, [safeSnapshots, activePeriod]);

  return (
    <div className="space-y-4">
      <div className="border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-violet-400" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-violet-400">
              {view === 'curve' ? 'Courbe LP' : 'Delta LP par jour'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* V8.11 : toggle vue */}
            <div className="flex border border-zinc-800">
              <button
                onClick={() => setView('curve')}
                className={`px-2.5 py-1 text-[10px] font-bold tracking-wider transition flex items-center gap-1 ${
                  view === 'curve' ? 'bg-violet-400 text-black' : 'bg-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                title="Courbe LP"
              >
                <LineIcon size={11} />
              </button>
              <button
                onClick={() => setView('delta')}
                className={`px-2.5 py-1 text-[10px] font-bold tracking-wider transition flex items-center gap-1 border-l border-zinc-800 ${
                  view === 'delta' ? 'bg-violet-400 text-black' : 'bg-transparent text-zinc-500 hover:text-zinc-300'
                }`}
                title="Delta par jour"
              >
                <BarChart3 size={11} />
              </button>
            </div>
            {/* Sélecteur période */}
            <div className="flex border border-zinc-800">
              {availablePeriods.map((p, idx) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-2.5 py-1 text-[10px] font-bold tracking-wider transition ${
                    idx > 0 ? 'border-l border-zinc-800' : ''
                  } ${
                    period === p.value
                      ? 'bg-violet-400 text-black'
                      : 'bg-transparent text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {view === 'curve'
          ? <LpHeroCurve points={lpPoints} periodLabel={activePeriod?.periodLabel || ''} />
          : <LpDailyDeltaChart points={lpPoints} />
        }
      </div>

      {/* v8.0 : Top 5 Champions WR + Insights du jour sous la courbe LP */}
      <DashboardBottomPanel
        games={games}
        version={version}
        userSettings={userSettings}
        onSuggestionClick={onSuggestionClick}
        onSeeAll={onSeeAll}
      />

      {/* Slot bloc actif */}
      {activeBlockSlot}
    </div>
  );
}
